import { Formations } from "@/api/privateRequests/squad-builder/get-formations";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { APIStatus } from "../../../api/APIStatus.type";
import { importSquad as _importSquad, getPlayers } from "../../../api/apiFc24";
import { buildSquads as _buildSquads } from "../../../api/privateRequests/squad-builder/build-squad";
import {
  MetaRating,
  getMetaRatings,
} from "../../../api/privateRequests/squad-builder/get-meta-ratings";
import {
  SavedOrSharedSquadResponseData,
  getSavedSquad,
} from "../../../api/privateRequests/squad-builder/get-saved-squad";
import { getSharedSquad } from "../../../api/privateRequests/squad-builder/get-shared-squad";
import {
  SavedSquad,
  getSquads,
} from "../../../api/privateRequests/squad-builder/get-squads";
import {
  SaveSquadRes,
  saveSquad as _saveSquad,
} from "../../../api/privateRequests/squad-builder/save-squad";
import { recommendPlayers } from "../../../api/publicRequests/squad-builder/recommendPlayers";
import { getCodeFromTactic as _getCodeFromTactic } from "../../../api/publicRequests/tactics/getCodeFromTactic";
import { getTacticFromCode as _getTacticFromCode } from "../../../api/publicRequests/tactics/getTacticFromCode";
import { FilterKey } from "../../../components/features/SquadBuilder/components/FiltersBar/FiltersBar";
import {
  SelectorFilterType,
  UpdateFilterType,
} from "../../../components/organisms/Filters/PlayerFilters/PlayerFilters";
import { ChemistryProfiles } from "../../../functions/calculateChemistry";
import { PlayerRole } from "../../players/playersSlice";
import { IRootState } from "../../store";
import { Tactic } from "../../tactics/tacticsSlice";
import {
  LeftPanelMode,
  PlayerFilter,
  RatingFilter,
  RightPanelMode,
  Scope,
  SquadBuilderMode,
  SquadBuilderPlayerType,
  calculateMetaRatingAndChemistry,
  defaultTactic,
  emptyPlayer,
  numberToMetal,
  prepareFiltesForAPI,
  setEmptyAttributes,
  setEmptyBench,
  setEmptyPlayerFilters,
  setEmptySquad,
} from "../squadBuilderUtils";
import { Squad } from "../squadBuilderUtils";

type DefaultSquadBuilderState = {
  squad: Squad;
  tempSquad: Squad;
  benchItems: SquadBuilderPlayerType[];
  tempBenchItems: SquadBuilderPlayerType[];
  builtSquads: Squad[];
  statusBuildSquad: APIStatus;
  statusGetRecommendedPlayers: APIStatus;
  statusImportSquad: APIStatus;
  statusSaveSquad: APIStatus;
  statusFetchSavedSquads: APIStatus;
  statusLoadSpecficSquad: APIStatus;
  statusInsertTacticCode: APIStatus;
  statusUpdateTacticCode: APIStatus;
  selectedCountries: number[];
  selectedLeagues: number[];
  selectedClubs: number[];
  selectedPnPs: number[];
  selectedVersions: number[];
  selectedMetals: number[];
  budget: number;
  scope: Scope;
  changingPitchItemOnIndex: number;
  recommendedPlayers: SquadBuilderPlayerType[];
  rightPanelMode: RightPanelMode;
  leftPanelMode: LeftPanelMode;
  playerFilters: PlayerFilter[];
  chosenTactic: Tactic;
  tempFormation?: string;
  chemistryProfiles: ChemistryProfiles;
  hasChangedSquad: boolean;
  savedSquads: SavedSquad[];
  loadedSaveSquadIndex: number;
  excludeIconsHeroes: boolean;
  ratingFilter: RatingFilter;
  playerRoles: PlayerRole[];
  formations: Formations;
  includeLoanPlayers: boolean;
};
const initialState: DefaultSquadBuilderState = {
  squad: {
    ...setEmptySquad(),
    players: setEmptySquad().players.map((p, index) => ({
      ...p,
      playerRoleId: defaultTactic.playerRoles[index].playerRoleId,
    })),
  },
  tempSquad: setEmptySquad(),
  benchItems: [],
  tempBenchItems: [],
  builtSquads: [],
  statusBuildSquad: "idle",
  statusGetRecommendedPlayers: "idle",
  statusImportSquad: "idle",
  statusSaveSquad: "idle",
  statusFetchSavedSquads: "idle",
  statusLoadSpecficSquad: "idle",
  statusInsertTacticCode: "idle",
  statusUpdateTacticCode: "idle",
  selectedCountries: [],
  selectedLeagues: [],
  selectedClubs: [],
  selectedPnPs: [],
  selectedMetals: [],
  selectedVersions: [],
  budget: Number.MAX_VALUE,
  scope: "market",
  changingPitchItemOnIndex: -1,
  recommendedPlayers: [],
  rightPanelMode: "default",
  leftPanelMode: "chem-overview",
  playerFilters: setEmptyPlayerFilters({
    playStylesFilterOptions: [],
    playStylesPlusFilterOptions: [],
  }),
  chosenTactic: defaultTactic,
  chemistryProfiles: {} as ChemistryProfiles,
  hasChangedSquad: false,
  savedSquads: [],
  loadedSaveSquadIndex: undefined,
  excludeIconsHeroes: false,
  ratingFilter: {},
  playerRoles: [],
  formations: {} as Formations,
  includeLoanPlayers: false,
};

export const buildSquads = createAsyncThunk<Squad[]>(
  "build-squads",
  async (_, { getState }) => {
    const state = getState() as IRootState;

    const {
      scope,
      budget,
      selectedCountries,
      selectedLeagues,
      selectedClubs,
      selectedPnPs,
      selectedVersions,
      selectedMetals,
      squad,
      chosenTactic,
      playerFilters,
      excludeIconsHeroes,
      ratingFilter,
      formations,
      benchItems,
      includeLoanPlayers,
    } = state.defaultSquadBuilderReducer;

    const inputPlayersToInclude: {
      playerRoleId: number;
      position: string;
      resourceId?: number;
      eaPlayerId?: number;
    }[] = [];

    const pitchIsFull = squad.players.every((p) => p.resourceId);
    for (let i = 0; i < 11; i++) {
      inputPlayersToInclude.push({
        playerRoleId: squad.players[i].playerRoleId,
        resourceId: pitchIsFull ? undefined : squad.players[i].resourceId,
        eaPlayerId: pitchIsFull ? undefined : squad.players[i].eaPlayerId,
        position:
          formations[chosenTactic.teamTactics.formationId]?.positions[i],
      });
    }
    // For bench players
    for (let i = 0; i < 7; i++) {
      inputPlayersToInclude.push({
        playerRoleId: benchItems[i].playerRoleId,
        resourceId: pitchIsFull ? undefined : benchItems[i].resourceId,
        eaPlayerId: pitchIsFull ? undefined : benchItems[i].eaPlayerId,
        position:
          benchItems[i]?.position ||
          formations[chosenTactic.teamTactics.formationId]?.positions[i + 11],
      });
    }

    const filters = prepareFiltesForAPI(playerFilters);
    const builtSquads = await _buildSquads({
      playersFrom: scope,
      players: inputPlayersToInclude,
      budget,
      benchBudget: budget * 0.1,
      manager: {
        country: squad.manager?.country || null,
        league: squad.manager?.league || null,
      },
      country: selectedCountries,
      league: selectedLeagues,
      club: selectedClubs,
      pastAndPresentClub: selectedPnPs,
      version: selectedVersions,
      metal: selectedMetals.map((metalId) => numberToMetal(metalId)),
      playerFilters: filters,
      formationId: chosenTactic.teamTactics.formationId,
      excludeIconsHeroes,
      minRating: ratingFilter.min,
      maxRating: ratingFilter.max,
      includeLoanPlayers,
    });

    // Squad builder doesn't return the reserve players, so we need to add them manually
    for (let i = 7; i < 12; i++) {
      builtSquads.data[i].players.push(benchItems[i - 7]);
    }

    const builtSquadsWithChemistry = builtSquads.data.map((ss) => {
      return {
        ...ss,
        suggestedSquadChemistry: ss.chemistry,
        chemistry: {},
      };
    }) as Squad[];

    return builtSquadsWithChemistry;
  },
);

const getRatingMinMax = (selectedMetals: number[]) => {
  let ratingMin: number | null = null;
  let ratingMax: number | null = null;

  if (selectedMetals.length > 0) {
    // Bronze (2)
    if (selectedMetals.includes(2)) {
      ratingMax = 64;
    }

    // Silver (1)
    if (selectedMetals.includes(1)) {
      if (ratingMax === null) {
        ratingMax = 74;
      }
      if (!selectedMetals.includes(2)) {
        ratingMin = 65;
      }
    }

    // Gold (0)
    if (selectedMetals.includes(0)) {
      ratingMin =
        selectedMetals.includes(1) || selectedMetals.includes(2) ? null : 75;
      ratingMax = 100;
    }
  }
  return { ratingMin, ratingMax };
};

export const fetchRecommendedPlayers = createAsyncThunk(
  "fetch-player-recs",
  async (_, { getState }) => {
    const state = getState() as IRootState;
    const {
      budget,
      squad,
      scope,
      changingPitchItemOnIndex,
      selectedClubs,
      selectedPnPs,
      selectedCountries,
      selectedLeagues,
      selectedVersions,
      selectedMetals,
      chosenTactic,
      playerFilters,
      excludeIconsHeroes,
      benchItems,
      formations,
      includeLoanPlayers,
    } = state.defaultSquadBuilderReducer;

    const { ratingMin, ratingMax } = getRatingMinMax(selectedMetals);

    // If bench player use getPlayers instead of recommendPlayers

    if (changingPitchItemOnIndex > 10) {
      let queryParams: any[] = [];
      if (selectedCountries.length > 0) {
        queryParams.push({ country: selectedCountries });
      }
      if (budget > 0 && budget < Number.MAX_VALUE) {
        queryParams.push({ priceMax: [budget] }, { priceMin: [1] });
      }
      if (selectedLeagues.length > 0) {
        queryParams.push({ league: selectedLeagues });
      }
      if (selectedClubs.length > 0) {
        queryParams.push({ club: selectedClubs });
      }
      if (selectedPnPs.length > 0) {
        queryParams.push({ "past-and-present-club": selectedPnPs });
      }
      if (selectedVersions.length > 0) {
        queryParams.push({ rarity: selectedVersions });
      }
      if (benchItems[changingPitchItemOnIndex - 11]?.position) {
        queryParams.push({
          position: [benchItems[changingPitchItemOnIndex - 11].position],
        });
      }
      if (ratingMin !== null) {
        queryParams.push({ ratingMin: [ratingMin] });
      }
      if (ratingMax !== null) {
        queryParams.push({ ratingMax: [ratingMax] });
      }
      if (excludeIconsHeroes) {
        queryParams.push({ "exclude-icons-heroes": [true] });
      }
      if (includeLoanPlayers) {
        queryParams.push({ "include-loan-players": [true] });
      }

      const res = await getPlayers({
        page: 1,
        sort: "meta-rating",
        myClub: scope === "club",
        // myEvolvedPlayers: true,
        playerRoleId: benchItems[changingPitchItemOnIndex - 11]?.playerRoleId,
        queryParams,
      });

      const convertedRes = res.players.map((player) => {
        const metaRatings = [
          {
            metaRating: player.metaRating,
            playerRoleId: player.playerRoleId,
            chemistry: 0,
            chemstyleId: 0,
          },
        ];
        return {
          ...player,
          possiblePositions: player.positions,
          metaRatings: metaRatings,
          playerRoleId: player.playerRoleId,
          position: benchItems[changingPitchItemOnIndex - 11]?.position,
        };
      }) as SquadBuilderPlayerType[];
      return convertedRes;
    }

    const playersReq = [];
    for (let i = 0; i < 11; i++) {
      playersReq.push({
        playerRoleId: squad.players[i].playerRoleId,
        resourceId: squad.players[i].resourceId,
        eaPlayerId: squad.players[i].eaPlayerId,
        position:
          formations[chosenTactic.teamTactics.formationId]?.positions[i],
      });
    }

    const filters = prepareFiltesForAPI(playerFilters);

    const res = await recommendPlayers({
      playerIndex: changingPitchItemOnIndex,
      budget,
      formationId: chosenTactic.teamTactics.formationId,
      players: playersReq,
      playersFrom: scope,
      manager: {
        country: squad.manager?.country || null,
        league: squad.manager?.league || null,
      },
      version: selectedVersions,
      metal: selectedMetals.map((metalId) => numberToMetal(metalId)),
      country: selectedCountries,
      league: selectedLeagues,
      club: selectedClubs,
      pastAndPresentClub: selectedPnPs,
      playerFilters: filters,
      excludeIconsHeroes: excludeIconsHeroes,
      includeLoanPlayers: includeLoanPlayers,
    });

    return res;
  },
);

export const getTacticFromCode = createAsyncThunk<
  Tactic,
  {
    code: string;
  }
>("get-tactic-from-code", async ({ code }) => {
  const res = await _getTacticFromCode({ code });
  return res;
});

export const getCodeFromTactic = createAsyncThunk<string, { tactic: Tactic }>(
  "get-code-from-tactic",
  async ({ tactic }) => {
    const res = await _getCodeFromTactic({ tactic });
    return res.eaCode;
  },
);

export const movePlayer = createAsyncThunk<
  void,
  {
    fromIndex: number;
    toIndex: number;
  }
>("move-player", async ({ fromIndex, toIndex }, { getState, dispatch }) => {
  const state = getState() as IRootState;
  const { squad, benchItems } = state.defaultSquadBuilderReducer;
  if (fromIndex === toIndex) {
    return;
  }
  const isMovingToBench = toIndex > 10;
  const isMovingFromBench = fromIndex > 10;
  const fromPlayer = isMovingFromBench
    ? benchItems[fromIndex - 11]
    : squad.players[fromIndex];
  const toPlayer = isMovingToBench
    ? benchItems[toIndex - 11]
    : squad.players[toIndex];

  dispatch(internalMovePlayer({ from: fromIndex, to: toIndex }));

  let metaRatingsFromPlayer: MetaRating[] = [];
  let metaRatingsToPlayer: MetaRating[] = [];

  if (toPlayer.resourceId) {
    [metaRatingsFromPlayer, metaRatingsToPlayer] = await Promise.all([
      getMetaRatings({
        resourceId: toPlayer.resourceId,
        playerRoleId: fromPlayer.playerRoleId,
        evoPath: toPlayer.evolutionPath?.join("-"),
        evoPathId: toPlayer.evoPathId,
        allChemStyles: 0,
      }),
      getMetaRatings({
        resourceId: fromPlayer.resourceId,
        playerRoleId: toPlayer.playerRoleId,
        evoPath: fromPlayer.evolutionPath?.join("-"),
        evoPathId: fromPlayer.evoPathId,
        allChemStyles: 0,
      }),
    ]);
  } else {
    metaRatingsToPlayer = await getMetaRatings({
      resourceId: fromPlayer.resourceId,
      playerRoleId: toPlayer.playerRoleId,
      evoPath: fromPlayer.evolutionPath?.join("-"),
      evoPathId: fromPlayer.evoPathId,
      allChemStyles: 0,
    });
  }

  const fromPlayerMetaRatings = metaRatingsFromPlayer.filter(
    (mr) =>
      mr.playerRoleId === fromPlayer.playerRoleId && mr.isBestChemstyleAtChem,
  );

  const toPlayerMetaRatings = metaRatingsToPlayer.filter(
    (mr) =>
      mr.playerRoleId === toPlayer.playerRoleId && mr.isBestChemstyleAtChem,
  );

  dispatch(
    updatePlayerMetaRatings({
      theDraggingPlayer: fromIndex,
      thePlayerBeingDraggedTo: toIndex,
      metaRatingsOfTheDraggingPlayer: isMovingToBench
        ? metaRatingsToPlayer
        : toPlayerMetaRatings.sort((a, b) => a.chemistry - b.chemistry),
      metaRatingsOfThePlayerBeingDraggedTo: isMovingFromBench
        ? metaRatingsFromPlayer
        : fromPlayerMetaRatings.sort((a, b) => a.chemistry - b.chemistry),
    }),
  );
  dispatch(updateChangingPitchItemOnIndex(-1));
});

export const importSquad = createAsyncThunk("import-squad", async () =>
  _importSquad(),
);

export const saveSquad = createAsyncThunk<
  SaveSquadRes,
  {
    squadName?: string;
  }
>("save-squad", async ({ squadName }, { getState }) => {
  const state = getState() as IRootState;
  const { squad, chosenTactic, savedSquads, loadedSaveSquadIndex, benchItems } =
    state.defaultSquadBuilderReducer;

  let saveSquadData = {
    name: squadName,
    formation: chosenTactic.teamTactics.formationId,
    players: [
      ...squad.players.map((p) => ({
        playerRoleId: p.playerRoleId,
        resourceId: p.resourceId,
        evoPath: p.evolutionPath?.join("-"),
        evoPathId: p.evoPathId,
      })),
      ...benchItems.map((p) => ({
        playerRoleId: p.playerRoleId,
        resourceId: p.resourceId,
        evoPath: p.evolutionPath?.join("-"),
        evoPathId: p.evoPathId,
      })),
    ],
    manager: {
      countryId: squad.manager?.country,
      leagueId: squad.manager?.league,
    },
    tactic: {
      name: chosenTactic.name,
      description: chosenTactic.description,
      logoUrl: chosenTactic.logoUrl,
      teamTactics: {
        buildUpStyle: chosenTactic.teamTactics.buildUpStyle,
        formationId: chosenTactic.teamTactics.formationId,
        defensiveApproach: chosenTactic.teamTactics.defensiveApproach,
        lineHeight: chosenTactic.teamTactics.lineHeight,
      },
      playerRoles: chosenTactic.playerRoles.map((p) => ({
        playerRoleId: p.playerRoleId,
        focus: p.focus,
      })),
    },
  };

  if (loadedSaveSquadIndex >= 0 && savedSquads[loadedSaveSquadIndex]?.id) {
    saveSquadData["squadId"] = savedSquads[loadedSaveSquadIndex]?.id;
    if (chosenTactic.id) {
      saveSquadData["tacticId"] = chosenTactic.id;
    }
  }

  const res = await _saveSquad(saveSquadData);
  return res;
});

export const fetchSquads = createAsyncThunk<SavedSquad[]>(
  "fetch-squads",
  async () => getSquads(),
);

export const fetchSpecificSquad = createAsyncThunk<
  {
    squad: SavedOrSharedSquadResponseData;
    index: number;
    sharedOrSaved: "shared" | "saved";
  },
  {
    index?: number;
    squadId?: string;
    sharedOrSaved: "shared" | "saved";
  }
>(
  "fetch-specific-squad",
  async ({ index, squadId, sharedOrSaved }, { getState }) => {
    const state = getState() as IRootState;

    let squad: SavedOrSharedSquadResponseData;
    if (sharedOrSaved === "shared") {
      squad = await getSharedSquad(squadId);
    } else {
      squad = await getSavedSquad(
        squadId ?? state.defaultSquadBuilderReducer.savedSquads[index]?.id,
      );
    }
    return { squad, index, sharedOrSaved };
  },
);
const getDefaultFocus = (playerRoles: PlayerRole[], playerRoleId: number) => {
  const playerRole = playerRoles.find((pr) => pr.id === playerRoleId);
  return playerRole?.focuses[0]?.name;
};

export const getPlayStyleFilterOptions = createAsyncThunk(
  "get-play-style-filter-options",
  async (_, { getState, dispatch }) => {
    const state = getState() as IRootState;
    const playStyleFilter = state.playersReducer.playerFilters.find(
      (f) => f.key === "playStyles",
    );
    const playStylePlusFilter = state.playersReducer.playerFilters.find(
      (f) => f.key === "playStylesPlus",
    );

    dispatch(
      loadPlayStyleFilterOptions({
        playStyleFilter: playStyleFilter as SelectorFilterType,
        playStylePlusFilter: playStylePlusFilter as SelectorFilterType,
      }),
    );
    return;
  },
);

export const setCustomTactic = (tactic: Tactic) => ({
  ...tactic,
  name: "Custom Tactic",
  tags: [],
  author: "",
  popularityPercentage: 0,
  socials: {},
  description:
    "You are now creating your own Custom Tactic. You can always chose a pre-made tactic from the FC IQ Tactics Overview",
  logoUrl: "http://cdn.easysbc.io/fc25/tactics/logos/preset2.png",
});

const extractPlayStylesFilterOptions = (playerFilters: PlayerFilter[]) => {
  // The options are the same for all players, so the index doesn't matter
  return {
    playStylesFilterOptions: (
      playerFilters[0].find((f) => f.key === "playStyles") as SelectorFilterType
    )?.options,
    playStylesPlusFilterOptions: (
      playerFilters[0].find(
        (f) => f.key === "playStylesPlus",
      ) as SelectorFilterType
    )?.options,
  };
};
const defaultSquadBuilderSlice = createSlice({
  name: "defaultSquadBuilder",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchRecommendedPlayers.pending, (state) => {
      state.statusGetRecommendedPlayers = "pending";
    });
    builder.addCase(fetchRecommendedPlayers.fulfilled, (state, action) => {
      state.statusGetRecommendedPlayers = "fulfilled";
      state.recommendedPlayers = action.payload;
    });
    builder.addCase(fetchRecommendedPlayers.rejected, (state) => {
      state.statusGetRecommendedPlayers = "rejected";
    });
    builder.addCase(buildSquads.pending, (state) => {
      state.statusBuildSquad = "pending";
    });
    builder.addCase(buildSquads.fulfilled, (state, action) => {
      state.statusBuildSquad = "fulfilled";
      const evoPlayers = state.squad.players.filter(
        (p) => p.evoPathId || p.evolutionPath,
      );

      state.builtSquads = [
        ...action.payload.map((s) => ({
          ...s,
          players: s.players.map((p) => {
            const evoPlayer = evoPlayers.find(
              (ep) => ep.resourceId === p.resourceId,
            );
            return { ...p, ...evoPlayer };
          }),
        })),
      ];
    });
    builder.addCase(buildSquads.rejected, (state) => {
      state.statusBuildSquad = "rejected";
    });
    builder
      .addCase(importSquad.fulfilled, (state, action) => {
        const { manager, players, tactic } = action.payload;

        state.statusImportSquad = "fulfilled";
        state.squad.manager = {
          league: manager.league,
          country: manager.country,
        };
        state.hasChangedSquad = true;
        const playerRoles = tactic?.playerRoles;
        let tacticPlayerRoles = [];
        if (tactic) {
          if (playerRoles.length < 23) {
            tacticPlayerRoles = playerRoles.map((p) => ({
              playerRoleId: p.playerRoleId,
              focus:
                p.focus || getDefaultFocus(state.playerRoles, p.playerRoleId),
            }));

            for (let i = 11; i < 23; i++) {
              let playerRoleId = {};
              if (players[i]?.resourceId) {
                playerRoleId = {
                  playerRoleId: players[i]?.metaRatings?.[0]?.playerRoleId,
                  focus: getDefaultFocus(
                    state.playerRoles,
                    players[i]?.metaRatings?.[0]?.playerRoleId,
                  ),
                };
              } else {
                playerRoleId = {
                  ...state.benchItems[i - 11],
                  focus: getDefaultFocus(
                    state.playerRoles,
                    state.benchItems[i - 11]?.playerRoleId,
                  ),
                };
              }
              tacticPlayerRoles.push(playerRoleId);
            }
          }
        }

        state.chosenTactic = setCustomTactic({
          ...tactic,
          playerRoles: tacticPlayerRoles,
        });
        const playersOnPitch = players.slice(0, 11);
        const playersOnBench = players.slice(11);
        const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
          playersOnPitch as SquadBuilderPlayerType[],
          state.squad.manager,
          state.chosenTactic.teamTactics.formationId,
          state.chemistryProfiles,
        );

        const importedPlayers = playersOnPitch.map((p, index) => ({
          ...p,
          owned: true,
          chemstyleId: p?.chemStyle || 250,
          playerRoleId: state.chosenTactic?.playerRoles?.[index].playerRoleId,
        })) as SquadBuilderPlayerType[];
        state.squad = {
          ...state.squad,
          players: [...importedPlayers],
          chemistry,
          metaRating: teamMetaRating,
        };

        state.benchItems = playersOnBench.map((p, i) => {
          if (!p.resourceId) {
            return state.benchItems[i];
          }
          const metaRating = p?.metaRatings?.[0].metaRating;
          const playerRoleId = p?.metaRatings?.[0].playerRoleId;

          const eligiblePositions =
            state.playerRoles.find(
              (pr) => pr.id === p?.metaRatings?.[0]?.playerRoleId,
            )?.eligiblePositions || [];
          const position = eligiblePositions.find(
            (pos) =>
              pos === p.preferredPosition || p.possiblePositions.includes(pos),
          );

          return {
            ...p,
            owned: true,
            metaRating,
            playerRoleId,
            position,
          };
        }) as SquadBuilderPlayerType[];
      })
      .addCase(importSquad.rejected, (state) => {
        state.statusImportSquad = "rejected";
      })
      .addCase(importSquad.pending, (state) => {
        state.statusImportSquad = "pending";
      })
      .addCase(
        saveSquad.fulfilled,
        (state, action: PayloadAction<SaveSquadRes>) => {
          state.statusSaveSquad = "fulfilled";
          state.hasChangedSquad = false;

          if (!state.chosenTactic.id) {
            state.chosenTactic.id = action.payload.tacticId;
          }

          // save squad to list if it's not already there
          if (!state.savedSquads.find((ss) => ss.id === action.payload.id)) {
            state.savedSquads = [
              ...state.savedSquads,
              { id: action.payload.id, name: action.payload.name },
            ];
            state.loadedSaveSquadIndex = state.savedSquads.length - 1;
          }
        },
      )
      .addCase(saveSquad.rejected, (state) => {
        state.statusSaveSquad = "rejected";
      })
      .addCase(saveSquad.pending, (state) => {
        state.statusSaveSquad = "pending";
      })
      .addCase(
        fetchSquads.fulfilled,
        (state, action: PayloadAction<SavedSquad[]>) => {
          state.savedSquads = action.payload;
          state.statusFetchSavedSquads = "fulfilled";
        },
      )
      .addCase(fetchSquads.rejected, (state) => {
        state.statusFetchSavedSquads = "rejected";
      })
      .addCase(fetchSquads.pending, (state) => {
        state.statusFetchSavedSquads = "pending";
      })
      .addCase(
        fetchSpecificSquad.fulfilled,
        (
          state,
          action: PayloadAction<{
            squad: SavedOrSharedSquadResponseData;
            index?: number;
          }>,
        ) => {
          const { squad, index } = action.payload;
          state.statusLoadSpecficSquad = "fulfilled";
          if (index >= 0) {
            state.loadedSaveSquadIndex = index;
            state.hasChangedSquad = false;
          } else {
            state.loadedSaveSquadIndex = undefined;
            state.hasChangedSquad = true;
          }

          const playerRoles = squad.tactic?.playerRoles;

          const playersOnPitch = squad.players.slice(0, 11);
          const benchPlayers = squad.players.slice(11);

          let tacticPlayerRoles = [];
          if (playerRoles.length < 23) {
            tacticPlayerRoles = playerRoles.map((p) => ({
              playerRoleId: p.playerRoleId,
              focus: getDefaultFocus(state.playerRoles, p.playerRoleId),
            }));

            for (let i = 11; i < 23; i++) {
              let playerRoleId = {};
              if (benchPlayers[i]?.resourceId) {
                playerRoleId = {
                  playerRoleId: benchPlayers[i]?.metaRatings?.[0]?.playerRoleId,
                  focus: getDefaultFocus(
                    state.playerRoles,
                    benchPlayers[i]?.metaRatings?.[0]?.playerRoleId,
                  ),
                };
              } else {
                playerRoleId = {
                  ...state.benchItems[i - 11],
                  focus: getDefaultFocus(
                    state.playerRoles,
                    state.benchItems[i - 11]?.playerRoleId,
                  ),
                };
              }
              tacticPlayerRoles.push(playerRoleId);
            }
          } else {
            tacticPlayerRoles = playerRoles;
          }
          const playersOnBench = [];
          for (let i = 0; i < 12; i++) {
            const p = benchPlayers[i];
            if (!p?.resourceId) {
              playersOnBench.push({
                playerRoleId: tacticPlayerRoles[i + 11]?.playerRoleId,
                position:
                  state.formations[state.chosenTactic.teamTactics.formationId]
                    .positions[i + 11],
              });
              continue;
            }

            const eligiblePositions =
              state.playerRoles.find(
                (a) => a.id === tacticPlayerRoles[i + 11]?.playerRoleId,
              )?.eligiblePositions || [];

            const position = eligiblePositions.find(
              (pos) =>
                pos === p.preferredPosition ||
                p.possiblePositions.includes(pos),
            );

            playersOnBench.push({
              ...p,
              metaRating: p?.metaRatings?.[0]?.metaRating,
              playerRoleId: tacticPlayerRoles[i + 11]?.playerRoleId,
              position,
            });
          }
          state.benchItems = playersOnBench;
          const players = [
            ...playersOnPitch.map((p, index) => ({
              ...p,
              playerRoleId: playerRoles[index]?.playerRoleId,
            })),
          ];

          const manager = {
            country: squad.manager?.countryId,
            league: squad.manager?.leagueId,
          };
          state.chosenTactic = {
            ...squad.tactic,
            playerRoles: tacticPlayerRoles,
          };

          const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
            players,
            manager,
            squad.tactic.teamTactics.formationId,
            state.chemistryProfiles,
          );

          state.squad = {
            ...state.squad,
            players,
            chemistry,
            manager,
            metaRating: teamMetaRating,
          };
        },
      )
      .addCase(getTacticFromCode.fulfilled, (state, action) => {
        let playerRoles = action.payload.playerRoles.map((pr) => ({
          playerRoleId: pr.playerRoleId,
          focus: pr.focus,
        }));

        playerRoles = [
          ...playerRoles,
          ...state.benchItems.map((p) => ({
            playerRoleId: p.playerRoleId,
            focus: getDefaultFocus(state.playerRoles, p.playerRoleId),
          })),
        ];

        state.chosenTactic = setCustomTactic({
          ...action.payload,
          playerRoles,
        });
        state.chosenTactic.eaCode = action.payload.eaCode;
        state.squad.players = state.squad.players.map((p, index) => ({
          ...p,
          playerRoleId: playerRoles[index]?.playerRoleId,
        }));
        const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
          state.squad.players,
          state.squad.manager,
          action.payload.teamTactics.formationId,
          state.chemistryProfiles,
        );
        state.squad = {
          ...state.squad,
          chemistry,
          metaRating: teamMetaRating,
        };
        state.statusInsertTacticCode = "fulfilled";
      })
      .addCase(getTacticFromCode.rejected, (state) => {
        state.statusInsertTacticCode = "rejected";
      })
      .addCase(getTacticFromCode.pending, (state) => {
        state.statusInsertTacticCode = "pending";
      })
      .addCase(getCodeFromTactic.fulfilled, (state, action) => {
        state.chosenTactic.eaCode = action.payload;
        state.statusUpdateTacticCode = "fulfilled";
      })
      .addCase(getCodeFromTactic.pending, (state) => {
        state.statusUpdateTacticCode = "pending";
      })
      .addCase(getCodeFromTactic.rejected, (state) => {
        state.statusUpdateTacticCode = "rejected";
      });
  },
  reducers: {
    loadFormations(
      state,
      action: {
        payload: Formations;
      },
    ) {
      state.formations = action.payload;
      const benchItems = [];
      for (let i = 0; i < 12; i++) {
        benchItems.push({
          ...emptyPlayer,
          position:
            state.formations[state.chosenTactic.teamTactics.formationId]
              .positions[i + 11],
          playerRoleId:
            state.formations[state.chosenTactic.teamTactics.formationId]
              ?.playerRoleIds[i + 11],
        });
      }
      state.benchItems = benchItems;
    },
    loadPlayerRoles(state, action: { payload: PlayerRole[] }) {
      state.playerRoles = action.payload;
    },
    loadChemistryProfiles(state, action: { payload: ChemistryProfiles }) {
      state.chemistryProfiles = action.payload;
    },
    updateFormation: (
      state,
      action: {
        payload: {
          formationId: string;
          isTemp?: boolean;
        };
      },
    ) => {
      if (action.payload.isTemp && action.payload.formationId === undefined) {
        state.tempSquad = setEmptySquad();
        state.tempFormation = undefined;
        return;
      }
      // instead of index get formationId
      const playerRolesForFormation =
        state.formations[action.payload.formationId].playerRoleIds;

      const playersWithNewRoles = state.squad.players.map((p, index) => ({
        ...p,
        playerRoleId: playerRolesForFormation[index],
      }));
      const benchItemsWithNewRoles = state.benchItems.map((p, index) => ({
        ...p,
        playerRoleId: playerRolesForFormation[index + 11],
        position:
          state.formations[action.payload.formationId].positions[index + 11],
        metaRating:
          p?.metaRatings?.[0]?.playerRoleId ===
          playerRolesForFormation[index + 11]
            ? p?.metaRatings?.[0]?.metaRating
            : 0,
      }));

      const tacticPlayerRoles = [
        ...playersWithNewRoles.map((p) => p.playerRoleId),
        ...benchItemsWithNewRoles.map((p) => p.playerRoleId),
      ];

      if (action.payload.isTemp) {
        state.tempFormation = action.payload.formationId;
        state.tempSquad.players = playersWithNewRoles;
        const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
          playersWithNewRoles,
          state.squad.manager,
          action.payload.formationId,
          state.chemistryProfiles,
        );
        state.tempSquad = {
          ...state.tempSquad,
          chemistry,
          metaRating: teamMetaRating,
        };
      } else {
        state.tempSquad = setEmptySquad();
        state.tempFormation = undefined;
        state.squad.players = playersWithNewRoles;
        state.benchItems = benchItemsWithNewRoles;
        state.chosenTactic = setCustomTactic({
          ...state.chosenTactic,
          playerRoles: tacticPlayerRoles.map((pr) => ({
            playerRoleId: pr,
            focus: getDefaultFocus(state.playerRoles, pr),
          })),
          teamTactics: {
            ...state.chosenTactic.teamTactics,
            formationId: action.payload.formationId,
          },
        });

        const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
          playersWithNewRoles,
          state.squad.manager,
          action.payload.formationId,
          state.chemistryProfiles,
        );
        state.squad = {
          ...state.squad,
          chemistry,
          metaRating: teamMetaRating,
        };
      }
    },
    internalMovePlayer(
      state,
      action: { payload: { from: number; to: number } },
    ) {
      const { chosenTactic, squad, benchItems } = state;
      const { players } = squad;
      const { from, to } = action.payload;

      let newPlayers = [...players];
      let newBenchItems = [...benchItems];

      const fromIsBench = from > 10;
      const toIsBench = to > 10;

      if (fromIsBench && toIsBench) {
        // Bench to bench
        const draggingPlayer = newBenchItems[from - 11];
        const potentialPlayer = newBenchItems[to - 11];
        newBenchItems[from - 11] = {
          ...potentialPlayer,
          playerRoleId: draggingPlayer.playerRoleId,
          position: draggingPlayer.position,
          chemistry: 0,
        };
        newBenchItems[to - 11] = {
          ...draggingPlayer,
          position: potentialPlayer.position,
          playerRoleId: potentialPlayer.playerRoleId,
        };
      } else if (!fromIsBench && !toIsBench) {
        // Squad to squad
        const draggingPlayer = newPlayers[from];
        newPlayers[from] = {
          ...newPlayers[to],
          playerRoleId: draggingPlayer.playerRoleId,
          chemIndex: from,
        };
        newPlayers[to] = {
          ...draggingPlayer,
          playerRoleId: newPlayers[to].playerRoleId,
          chemIndex: to,
        };
      } else if (fromIsBench && !toIsBench) {
        // Bench to squad
        const draggingPlayer = newBenchItems[from - 11];
        newBenchItems[from - 11] = {
          ...newPlayers[to],
          playerRoleId: draggingPlayer.playerRoleId,
          position: draggingPlayer.position,
        };
        newPlayers[to] = {
          ...draggingPlayer,
          playerRoleId: newPlayers[to].playerRoleId,
          chemIndex: to,
        };
      } else {
        // Squad to bench
        const draggingPlayer = newPlayers[from];
        newPlayers[from] = {
          ...newBenchItems[to - 11],
          playerRoleId: draggingPlayer.playerRoleId,
          chemIndex: from,
        };
        newBenchItems[to - 11] = {
          ...draggingPlayer,
          playerRoleId: newBenchItems[to - 11].playerRoleId,
          position: newBenchItems[to - 11].position,
          chemistry: 0,
        };
      }

      const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
        newPlayers,
        state.squad.manager,
        chosenTactic.teamTactics.formationId,
        state.chemistryProfiles,
      );
      state.hasChangedSquad = true;
      state.squad = {
        ...state.squad,
        players: newPlayers,
        chemistry,
        metaRating: teamMetaRating,
      };
      state.benchItems = newBenchItems;
    },
    addPlayerOrChangeRole(
      state,
      action: {
        payload: {
          playerRoleId?: number;
          focus?: string;
          player?: SquadBuilderPlayerType;
          temp?: boolean;
          metaRatings?: MetaRating[];
          position?: string;
        };
      },
    ) {
      if (state.changingPitchItemOnIndex > 10) {
        if (action.payload.temp) {
          return;
        }
        const player =
          action.payload.player ||
          state.benchItems[state.changingPitchItemOnIndex - 11];

        let metaRating = 0;
        let metaRatings = [];

        if (
          action.payload.position &&
          player.possiblePositions?.includes(action.payload.position)
        ) {
          metaRatings = action.payload.metaRatings;
          metaRating = action.payload.metaRatings[0].metaRating;
        } else if (!action.payload.position) {
          metaRatings = player.metaRatings;
          metaRating = player.metaRatings?.[0]?.metaRating || 0;
        }
        const playerRoleId =
          action.payload.playerRoleId ||
          state.benchItems[state.changingPitchItemOnIndex - 11].playerRoleId;
        state.benchItems[state.changingPitchItemOnIndex - 11] = {
          ...(player?.resourceId && player),
          position:
            action.payload.position ||
            state.benchItems[state.changingPitchItemOnIndex - 11].position,
          playerRoleId,
          ...(metaRatings && {
            metaRatings,
          }),
          metaRating,
        };
        state.chosenTactic.playerRoles[state.changingPitchItemOnIndex] = {
          playerRoleId:
            action.payload.playerRoleId ||
            state.benchItems[state.changingPitchItemOnIndex - 11].playerRoleId,
          focus: getDefaultFocus(state.playerRoles, playerRoleId),
        };
        state.chosenTactic = setCustomTactic(state.chosenTactic);
        state.hasChangedSquad = true;
        return;
      }
      const { squad, chosenTactic } = state;
      const { manager, players } = squad;
      let newPlayers = [...players];

      let player: SquadBuilderPlayerType;
      if (action.payload.player) {
        // adding player
        player = {
          ...action.payload.player,
          playerRoleId:
            state.squad.players[state.changingPitchItemOnIndex]?.playerRoleId,
        };
      } else if (action.payload.playerRoleId) {
        // changing player role
        player = {
          ...state.squad.players[state.changingPitchItemOnIndex],
          playerRoleId: action.payload.playerRoleId,
          metaRatings: action.payload.metaRatings,
        };
      } else if (action.payload.metaRatings) {
        // changing chemstyle
        player = {
          ...state.squad.players[state.changingPitchItemOnIndex],
          metaRatings: action.payload.metaRatings,
        };
      }
      // adding the new player before updating meta rating and chemistry
      newPlayers[state.changingPitchItemOnIndex] = {
        ...player,
      };

      const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
        newPlayers,
        manager,
        chosenTactic.teamTactics.formationId,
        state.chemistryProfiles,
      );

      player = {
        ...player,
        chemistry: chemistry.playersChemistry[state.changingPitchItemOnIndex],
        chemstyleId:
          player.metaRatings?.[
            chemistry.playersChemistry[state.changingPitchItemOnIndex]
          ]?.chemstyleId,
      };
      // adding the updated player to the squad
      newPlayers[state.changingPitchItemOnIndex] = {
        ...player,
      };

      if (action.payload.temp) {
        const tempPlayers = setEmptySquad().players;
        tempPlayers[state.changingPitchItemOnIndex] = { ...player };
        state.tempSquad = {
          ...squad,
          players: tempPlayers,
          chemistry,
          metaRating: teamMetaRating,
        };
      } else {
        state.hasChangedSquad = true;
        state.squad = {
          ...squad,
          players: newPlayers,
          chemistry,
          metaRating: teamMetaRating,
        };
        state.tempSquad = setEmptySquad();
      }

      // updating tactic specifically
      if (
        (action.payload.playerRoleId || action.payload.focus) &&
        !action.payload.player &&
        !action.payload.temp
      ) {
        let focus: string;
        if (action.payload.focus) {
          focus = action.payload.focus;
        } else {
          // find default focus for the new player role
          focus = getDefaultFocus(state.playerRoles, player.playerRoleId);
        }

        state.chosenTactic.playerRoles[state.changingPitchItemOnIndex] = {
          playerRoleId: player.playerRoleId,
          focus,
        };
        state.chosenTactic = setCustomTactic(state.chosenTactic);
      }
    },
    removePlayer(
      state,
      action: {
        payload: {
          index?: number;
          temp?: boolean;
        };
      },
    ) {
      const temp = action.payload.temp;
      if (temp) {
        state.tempSquad = setEmptySquad();
        return;
      }

      if (action.payload.index > 10) {
        state.benchItems[action.payload.index - 11] = {
          ...emptyPlayer,
          playerRoleId:
            state.benchItems[action.payload.index - 11]?.playerRoleId,
          position: state.benchItems[action.payload.index - 11]?.position,
        };
        return;
      }

      const { squad, chosenTactic } = state;
      const { players, manager } = squad;
      const newPlayers = [...players];
      const index =
        action.payload.index >= 0
          ? action.payload.index
          : state.changingPitchItemOnIndex;

      newPlayers[index] = {
        ...emptyPlayer,
        playerRoleId: state.squad.players[index]?.playerRoleId,
      };

      const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
        newPlayers,
        manager,
        chosenTactic.teamTactics.formationId,
        state.chemistryProfiles,
      );

      const newSquad = {
        ...state.squad,
        players: newPlayers,
        chemistry,
        metaRating: teamMetaRating,
      };
      state.hasChangedSquad = true;
      state.squad = newSquad;
    },
    resetRole(state, action: { payload: number }) {
      const chosenTactic = state.chosenTactic;
      state.squad.players[action.payload] = {
        ...emptyPlayer,
        playerRoleId:
          state.formations[chosenTactic.teamTactics.formationId]?.playerRoleIds[
            action.payload
          ],
      };
      state.hasChangedSquad = true;
    },
    selectChemStyle(
      state,
      action: {
        payload: {
          chemstyleId: number;
          metaRatings: MetaRating[];
        };
      },
    ) {
      state.squad.players[state.changingPitchItemOnIndex].chemstyleId =
        action.payload.chemstyleId;
      state.squad.players[state.changingPitchItemOnIndex].metaRatings =
        action.payload.metaRatings;
    },
    addSquad(
      state,
      action: {
        payload: {
          index: number;
          temp?: boolean;
        };
      },
    ) {
      const index = action.payload.index;
      const temp = action.payload.temp;
      const { chosenTactic } = state;
      const chosenSquad = { ...state.builtSquads[index] };
      const { manager, players } = chosenSquad;

      const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
        players,
        manager,
        chosenTactic.teamTactics.formationId,
        state.chemistryProfiles,
      );
      const playersOnPitch: SquadBuilderPlayerType[] = [];
      const playersOnBench: SquadBuilderPlayerType[] = [...state.benchItems];

      for (let i = 0; i < 23; i++) {
        if (i < 11) {
          playersOnPitch.push({
            ...chosenSquad.players[i],
            chemistry: chemistry.playersChemistry[i],
            playerRoleId: state.squad.players[i].playerRoleId,
          });
        } else if (i > 10 && i < 18) {
          playersOnBench[i - 11] = {
            ...chosenSquad.players[i],
            chemistry: chemistry.playersChemistry[i],
            playerRoleId: state.benchItems[i - 11].playerRoleId,
            position: state.benchItems[i - 11].position,
            metaRating: chosenSquad.players[i].metaRatings[0]?.metaRating || 0,
          };
        } else {
          // use the reserves from before the squad was built
          playersOnBench[i - 11] = {
            ...state.benchItems[i - 11],
            playerRoleId: state.benchItems[i - 11].playerRoleId,
            position: state.benchItems[i - 11].position,
          };
        }
      }

      if (temp) {
        state.tempSquad = {
          ...chosenSquad,
          players: playersOnPitch,
          chemistry,
          metaRating: teamMetaRating,
          manager,
        };
        state.tempBenchItems = playersOnBench;
      } else {
        state.squad = {
          ...chosenSquad,
          players: playersOnPitch,
          chemistry,
          metaRating: teamMetaRating,
          manager,
        };
        state.benchItems = playersOnBench;
        state.statusBuildSquad = "idle";
        state.tempSquad = setEmptySquad();
        state.tempBenchItems = [];
        state.hasChangedSquad = true;
      }
    },
    removeTempSquad(state) {
      state.tempSquad = setEmptySquad();
      state.tempBenchItems = [];
    },
    clearSquad(state) {
      state.playerFilters = setEmptyPlayerFilters(
        extractPlayStylesFilterOptions(state.playerFilters),
      );
      state.squad = setEmptySquad(state.chosenTactic.playerRoles.slice(0, 11));
      state.benchItems = setEmptyBench({
        playerRoles: state.benchItems.map((p) => ({
          playerRoleId: p.playerRoleId,
          position: p.position,
        })),
      });
      state.hasChangedSquad = false;
      state.tempSquad = setEmptySquad();
      state.tempBenchItems = [];
      state.loadedSaveSquadIndex = undefined;
      state.statusBuildSquad = "idle";
    },
    exitPickSquad(state) {
      state.statusBuildSquad = "idle";
    },
    setSaveSquadStatusToIdle(state) {
      state.statusSaveSquad = "idle";
    },
    updateChangingPitchItemOnIndex(state, action: { payload: number }) {
      state.changingPitchItemOnIndex = action.payload;
    },
    updateBudget(state, action: { payload: number }) {
      state.budget = action.payload;
    },
    updateScope(state, action: { payload: Scope }) {
      state.scope = action.payload;
    },
    updateExcludeIconsHeroes(state, action: { payload: boolean }) {
      state.excludeIconsHeroes = action.payload;
    },
    updateIncludeLoanPlayers(state, action: { payload: boolean }) {
      state.includeLoanPlayers = action.payload;
      // if state.scope is both, we just keep the scope as both
      if (state.scope === "market") {
        state.scope = "club";
      }
    },
    updateMinRating(state, action: { payload: number }) {
      state.ratingFilter = {
        ...state.ratingFilter,
        min: action.payload,
      };
    },
    updateMaxRating(state, action: { payload: number }) {
      state.ratingFilter = {
        ...state.ratingFilter,
        max: action.payload,
      };
    },
    updateLoadedSquadIndex(state, action: { payload: number }) {
      state.loadedSaveSquadIndex = action.payload;
      state.statusLoadSpecficSquad = "idle";
      state.statusSaveSquad = "idle";
    },
    updateManager(
      state,
      action: { payload: { id: number; type: "league" | "country" } },
    ) {
      const newManager = { ...state.squad.manager };
      if (action.payload.type === "league") {
        if (state.squad.manager.league === action.payload.id) {
          newManager["league"] = null;
        } else {
          newManager["league"] = action.payload.id;
        }
      } else {
        if (state.squad.manager.country === action.payload.id) {
          newManager["country"] = null;
        } else {
          newManager["country"] = action.payload.id;
        }
      }
      state.squad.manager = {
        ...newManager,
      };

      const { chosenTactic } = state;

      const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
        state.squad.players,
        newManager,
        chosenTactic.teamTactics.formationId,
        state.chemistryProfiles,
      );

      state.squad = {
        ...state.squad,
        chemistry,
        metaRating: teamMetaRating,
      };
    },
    updatePlayerMetaRatings(
      state,
      action: {
        payload: {
          theDraggingPlayer: number;
          thePlayerBeingDraggedTo: number;
          metaRatingsOfTheDraggingPlayer: MetaRating[];
          metaRatingsOfThePlayerBeingDraggedTo: MetaRating[];
        };
      },
    ) {
      const {
        theDraggingPlayer,
        thePlayerBeingDraggedTo,
        metaRatingsOfTheDraggingPlayer,
        metaRatingsOfThePlayerBeingDraggedTo,
      } = action.payload;

      const isMovingToBench = thePlayerBeingDraggedTo > 10;
      const isMovingFromBench = theDraggingPlayer > 10;

      if (isMovingToBench) {
        const playerIndexOne = thePlayerBeingDraggedTo - 11;
        state.benchItems[playerIndexOne] = {
          ...state.benchItems[playerIndexOne],
          metaRating: metaRatingsOfTheDraggingPlayer[0]?.metaRating || 0,
          chemistry: metaRatingsOfTheDraggingPlayer[0]?.chemistry || 0,
        };
      } else {
        const chemistry =
          state.squad.players[thePlayerBeingDraggedTo]?.chemistry ||
          state.squad.chemistry.playersChemistry?.[thePlayerBeingDraggedTo];

        state.squad.players[thePlayerBeingDraggedTo] = {
          ...state.squad.players[thePlayerBeingDraggedTo],
          metaRating:
            metaRatingsOfTheDraggingPlayer[chemistry]?.metaRating || 0,
          metaRatings: metaRatingsOfTheDraggingPlayer,
          chemstyleId: metaRatingsOfTheDraggingPlayer[chemistry]?.chemstyleId,
          chemistry,
        };
      }

      if (isMovingFromBench) {
        const playerIndexTwo = theDraggingPlayer - 11;
        state.benchItems[playerIndexTwo] = {
          ...state.benchItems[playerIndexTwo],
          metaRating: metaRatingsOfThePlayerBeingDraggedTo[0]?.metaRating || 0,
          chemistry: metaRatingsOfThePlayerBeingDraggedTo[0]?.chemistry || 0,
        };
      } else {
        const chemistry =
          state.squad.players[theDraggingPlayer]?.chemistry ||
          state.squad.chemistry.playersChemistry?.[theDraggingPlayer];

        state.squad.players[theDraggingPlayer] = {
          ...state.squad.players[theDraggingPlayer],
          metaRating:
            metaRatingsOfThePlayerBeingDraggedTo[chemistry]?.metaRating || 0,
          metaRatings: metaRatingsOfThePlayerBeingDraggedTo,
          chemstyleId:
            metaRatingsOfThePlayerBeingDraggedTo[chemistry]?.chemstyleId,
          chemistry,
        };
      }

      state.hasChangedSquad = true;
      const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
        state.squad.players,
        state.squad.manager,
        state.chosenTactic.teamTactics.formationId,
        state.chemistryProfiles,
      );
      state.squad = {
        ...state.squad,
        chemistry,
        metaRating: teamMetaRating,
      };
    },
    updateCustomFilter: (state, action: PayloadAction<UpdateFilterType>) => {
      const { key } = action.payload;

      const playerIndex = state.changingPitchItemOnIndex;

      const filter = state.playerFilters[playerIndex]?.find(
        (f) => f.key === key,
      );

      if (filter?.type === "RANGE_FILTER") {
        const { min, max } = action.payload;
        if (min !== undefined) filter.minValue = min;
        if (max !== undefined) filter.maxValue = max;
      } else if (filter?.type === "SELECTOR_FILTER") {
        const { value } = action.payload;
        const isSelected = filter.selectedOptions.includes(value);

        if (isSelected)
          filter.selectedOptions = filter.selectedOptions.filter(
            (so) => so !== value,
          );
        else filter.selectedOptions.push(value);
      }
    },
    clearCustomFilter(state, action: PayloadAction<string>) {
      const filter = state.playerFilters[state.changingPitchItemOnIndex].find(
        (f) => f.key === action.payload,
      );

      if (filter?.type === "RANGE_FILTER") {
        filter.minValue = null;
        filter.maxValue = null;
      } else if (filter?.type === "SELECTOR_FILTER") {
        filter.selectedOptions = [];
      }
    },
    clearPlayerFilters(state, action: PayloadAction<number>) {
      state.playerFilters[action.payload] = setEmptyAttributes(
        extractPlayStylesFilterOptions(state.playerFilters),
      );
    },
    clearFilter(state, action: PayloadAction<FilterKey>) {
      switch (action.payload) {
        case "budget":
          state.budget = Number.MAX_VALUE;
          break;
        case "country":
          state.selectedCountries = [];
          break;
        case "league":
          state.selectedLeagues = [];
          break;
        case "clubs":
          state.selectedClubs = [];
          break;
        case "rarity":
          state.selectedVersions = [];
          break;
        case "market":
          state.scope = "market";
          break;
        case "both":
          state.scope = "market";
          break;
        case "club":
          state.scope = "market";
          break;
        case "playerFilter":
          state.playerFilters = setEmptyPlayerFilters(
            extractPlayStylesFilterOptions(state.playerFilters),
          );
          break;
        case "pastAndPresentClub":
          state.selectedPnPs = [];
          break;
        case "group":
          state.selectedCountries = [];
          state.selectedLeagues = [];
          state.selectedClubs = [];
          state.selectedVersions = [];
          state.selectedPnPs = [];
          state.selectedMetals = [];
          break;
        default:
          break;
      }
    },
    updateSelectedCountries(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedCountries = [];
        return;
      }
      const id = action.payload;
      if (state.selectedCountries.includes(id))
        state.selectedCountries = state.selectedCountries.filter(
          (c) => c !== id,
        );
      else state.selectedCountries = [...state.selectedCountries, id];
    },
    updateSelectedLeagues(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedLeagues = [];
        return;
      }
      const id = action.payload;
      if (state.selectedLeagues.includes(id))
        state.selectedLeagues = state.selectedLeagues.filter((c) => c !== id);
      else state.selectedLeagues = [...state.selectedLeagues, id];
    },
    updateSelectedClubs(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedClubs = [];
        return;
      }
      const id = action.payload;
      if (state.selectedClubs.includes(id))
        state.selectedClubs = state.selectedClubs.filter((c) => c !== id);
      else state.selectedClubs = [...state.selectedClubs, id];
    },
    updateSelectedPnPs(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedPnPs = [];
        return;
      }
      const id = action.payload;
      if (state.selectedPnPs.includes(id))
        state.selectedPnPs = state.selectedPnPs.filter((c) => c !== id);
      else state.selectedPnPs = [...state.selectedPnPs, id];
    },
    updateSelectedVersions(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedVersions = [];
        return;
      }
      const id = action.payload;
      if (state.selectedVersions.includes(id))
        state.selectedVersions = state.selectedVersions.filter((c) => c !== id);
      else state.selectedVersions = [...state.selectedVersions, id];
    },
    updateSelectedMetals(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedMetals = [];
        return;
      }
      const id = action.payload;
      if (state.selectedMetals.includes(id))
        state.selectedMetals = state.selectedMetals.filter((c) => c !== id);
      else state.selectedMetals = [...state.selectedMetals, id];
    },
    sortBuiltSquads(state, action: { payload: "meta-rating" | "chemistry" }) {
      state.builtSquads = [...state.builtSquads].sort((a, b) => {
        if (action.payload === "meta-rating") {
          return b.metaRating - a.metaRating;
        } else {
          return b.suggestedSquadChemistry - a.suggestedSquadChemistry;
        }
      });
    },
    switchRightPanelMode(state, action: { payload: RightPanelMode }) {
      state.rightPanelMode = action.payload;
    },
    switchLeftPanelMode(state, action: { payload: LeftPanelMode }) {
      state.leftPanelMode = action.payload;
    },
    setChosenTactic(
      state,
      action: {
        payload: {
          tactic: Tactic;
          fromTacticsPage: boolean;
          initialLoad?: boolean;
        };
      },
    ) {
      let playerRoles: { playerRoleId: number; focus: string }[];

      if (action.payload.fromTacticsPage) {
        playerRoles = action.payload.tactic.playerRoles?.map((pr) => ({
          playerRoleId: pr.playerRoleId,
          focus: pr.focus,
        }));
        state.benchItems.forEach((bi) => {
          playerRoles.push({
            playerRoleId: bi.playerRoleId,
            focus: getDefaultFocus(state.playerRoles, bi.playerRoleId),
          });
        });
      } else if (
        state.chosenTactic?.teamTactics?.formationId !==
        action.payload.tactic.teamTactics.formationId
      ) {
        playerRoles = state.formations[
          action.payload.tactic.teamTactics.formationId
        ].playerRoleIds.map((pr) => ({
          playerRoleId: pr,
          focus: getDefaultFocus(state.playerRoles, pr),
        }));
      } else {
        playerRoles = action.payload.tactic.playerRoles.map((pr) => ({
          playerRoleId: pr.playerRoleId,
          focus: pr.focus,
        }));
      }

      let defensiveApproach =
        action.payload.tactic.teamTactics.defensiveApproach;
      let lineHeight = action.payload.tactic.teamTactics.lineHeight;

      if (
        action.payload.tactic.teamTactics.lineHeight !==
        state.chosenTactic.teamTactics.lineHeight
      ) {
        if (action.payload.tactic.teamTactics.lineHeight <= 30) {
          defensiveApproach = "Deep";
        } else if (
          action.payload.tactic.teamTactics.lineHeight <= 59 &&
          action.payload.tactic.teamTactics.lineHeight > 30
        ) {
          defensiveApproach = "Balanced";
        } else if (
          action.payload.tactic.teamTactics.lineHeight <= 89 &&
          action.payload.tactic.teamTactics.lineHeight > 59
        ) {
          defensiveApproach = "High";
        } else {
          defensiveApproach = "Aggressive";
        }
      } else if (
        action.payload.tactic.teamTactics.defensiveApproach !==
        state.chosenTactic.teamTactics.defensiveApproach
      ) {
        if (action.payload.tactic.teamTactics.defensiveApproach === "Deep") {
          lineHeight = 25;
        } else if (
          action.payload.tactic.teamTactics.defensiveApproach === "Balanced"
        ) {
          lineHeight = 50;
        } else if (
          action.payload.tactic.teamTactics.defensiveApproach === "High"
        ) {
          lineHeight = 70;
        } else {
          lineHeight = 95;
        }
      }
      state.squad.players = [
        ...state.squad.players.map((p, index) => ({
          ...p,
          playerRoleId: playerRoles[index].playerRoleId,
        })),
      ];
      if (action.payload.fromTacticsPage) {
        const { id, ...rest } = action.payload.tactic;
        state.chosenTactic = {
          ...rest,
          playerRoles,
        };
      } else if (!action.payload.initialLoad) {
        state.chosenTactic = setCustomTactic({
          ...action.payload.tactic,
          teamTactics: {
            ...action.payload.tactic.teamTactics,
            defensiveApproach,
            lineHeight,
          },
          playerRoles,
        });
        state.hasChangedSquad = true;
      }
    },
    updateTacticInsertStatus(state, action: { payload: APIStatus }) {
      state.statusInsertTacticCode = action.payload;
    },
    clearRecommendedPlayers(state) {
      state.recommendedPlayers = [];
    },
    loadPlayStyleFilterOptions(
      state,
      action: {
        payload: {
          playStyleFilter: SelectorFilterType;
          playStylePlusFilter: SelectorFilterType;
        };
      },
    ) {
      state.playerFilters.forEach((filter) => {
        const convertedPlaystyleOptions = Object.values(
          action.payload.playStyleFilter.options,
        ).map((playStyle) => ({
          id: playStyle.id,
          label: playStyle.label,
          value: playStyle.id,
          category: playStyle.category,
        }));

        const convertedPlaystylePlusOptions = Object.values(
          action.payload.playStylePlusFilter.options,
        ).map((playStyle) => ({
          id: playStyle.id,
          label: playStyle.label,
          value: playStyle.id,
          category: playStyle.category,
        }));
        filter.forEach((f) => {
          if (f.key === "playStyles" && "options" in f) {
            f.options = convertedPlaystyleOptions;
          } else if (f.key === "playStylesPlus" && "options" in f) {
            f.options = convertedPlaystylePlusOptions;
          }
        });
      });
    },
  },
});

export const defaultSquadBuilderSelector = (state: IRootState) =>
  state.defaultSquadBuilderReducer;

export const teamValueSelector =
  ({ mode }: { mode: SquadBuilderMode }) =>
  (state: IRootState) => {
    let squad: Squad = setEmptySquad();
    let tempSquad: Squad = setEmptySquad();

    if (mode === "default") {
      squad = state.defaultSquadBuilderReducer.squad;
      tempSquad = state.defaultSquadBuilderReducer.tempSquad;
    }
    // else {
    //   squad = state.bestFormationReducer.squad;
    //   tempSquad = state.bestFormationReducer.tempSquad;
    // }
    const { players: tempPlayers } = tempSquad;
    const { players } = squad;

    let teamValue = 0;
    let tempTeamValue = 0;
    let ownedTeamValue = 0;
    let tempOwnedTeamValue = 0;
    for (let i = 0; i < 11; i++) {
      const player = players[i];
      const tempPlayer = tempPlayers[i];

      teamValue += player?.price || player?.sbcPrice || 0;
      tempTeamValue += tempPlayer?.price || tempPlayer?.sbcPrice || 0;
      ownedTeamValue +=
        (player?.owned && (player?.price || player?.sbcPrice)) || 0;
      tempOwnedTeamValue +=
        (tempPlayer?.owned && (tempPlayer?.price || tempPlayer?.sbcPrice)) || 0;
    }

    return {
      teamValue,
      tempTeamValue,
      ownedTeamValue,
      tempOwnedTeamValue,
    };
  };

export const numberOfPlayersWithFiltersSelector = (state: IRootState) => {
  const { playerFilters } = state.defaultSquadBuilderReducer;

  let count = 0;
  playerFilters.forEach((filter) => {
    let foundFilter = false;
    filter.forEach((f) => {
      if (f.type === "SELECTOR_FILTER") {
        if (f.selectedOptions.length) {
          foundFilter = true;
          return;
        }
      } else {
        if (f.minValue || f.maxValue) {
          foundFilter = true;
          return;
        }
      }
    });
    if (foundFilter) count++;
  });

  return count;
};

export const {
  updateFormation,
  updateBudget,
  updateExcludeIconsHeroes,
  updateMinRating,
  updateMaxRating,
  updateScope,
  updateSelectedCountries,
  updateSelectedLeagues,
  updateSelectedClubs,
  updateSelectedPnPs,
  updateSelectedMetals,
  updateChangingPitchItemOnIndex,
  loadFormations,
  loadChemistryProfiles,
  addPlayerOrChangeRole,
  switchRightPanelMode,
  removePlayer,
  resetRole,
  updateSelectedVersions,
  clearSquad,
  internalMovePlayer,
  sortBuiltSquads,
  addSquad,
  removeTempSquad,
  exitPickSquad,
  updateManager,
  selectChemStyle,
  updatePlayerMetaRatings,
  clearPlayerFilters,
  updateCustomFilter,
  clearCustomFilter,
  switchLeftPanelMode,
  setChosenTactic,
  clearFilter,
  setSaveSquadStatusToIdle,
  updateLoadedSquadIndex,
  loadPlayerRoles,
  updateTacticInsertStatus,
  clearRecommendedPlayers,
  updateIncludeLoanPlayers,
  loadPlayStyleFilterOptions,
} = defaultSquadBuilderSlice.actions;

export default defaultSquadBuilderSlice.reducer;
